import { type PersistedSessionLite, SessionSource } from '@proton/shared/lib/authentication/SessionInterface';
import { AccessType } from '@proton/shared/lib/authentication/accessType';
import {
    getMinimalPersistedSession,
    getPersistedSessions,
} from '@proton/shared/lib/authentication/persistedSessionStorage';
import { getCookie, setCookie } from '@proton/shared/lib/helpers/cookies';
import { stringToUint8Array, uint8ArrayToString } from '@proton/shared/lib/helpers/encoding';
import { getSecondLevelDomain } from '@proton/shared/lib/helpers/url';
import isEnumValue from '@proton/utils/isEnumValue';
import isTruthy from '@proton/utils/isTruthy';

const cookieName = 'iaas';

const syncToCookie = (cookieValue: string) => {
    setCookie({
        cookieName: cookieName,
        cookieValue,
        cookieDomain: getSecondLevelDomain(window.location.hostname),
        path: '/',
        expirationDate: 'max',
    });
};

type SerializedItem =
    | {
          l: number;
          s: 0 | 1; // Legacy value, not used anymore. TODO: Remove after all web clients are deployed and understand it.
          a: AccessType;
      }
    | number;

const toItem = (value: PersistedSessionLite): SerializedItem => {
    if (value.accessType === AccessType.Self) {
        return value.localID;
    }
    return {
        l: value.localID,
        a: value.accessType,
        s: 0 /* Legacy clients only understand non-self admin-access (0). . TODO: Remove after all web clients are deployed and understand it */,
    };
};

const fromItem = (value: any): PersistedSessionLite | undefined => {
    if (Number.isInteger(value)) {
        return { localID: value, accessType: AccessType.Self };
    }
    if ('l' in value) {
        if ('a' in value) {
            return {
                localID: Number(value.l),
                accessType: isEnumValue(value.a, AccessType) ? value.a : AccessType.Self,
            };
        }
        /* Legacy value. TODO: Remove after all web clients are deployed and understand it. */
        if ('s' in value) {
            return {
                localID: Number(value.l),
                accessType: Boolean(value.s) ? AccessType.Self : AccessType.AdminAccess,
            };
        }
    }
};

const to = (value: PersistedSessionLite[]) => {
    return stringToUint8Array(JSON.stringify(value.map(toItem))).toBase64({ alphabet: 'base64url', omitPadding: true });
};

const from = (value: string | undefined): PersistedSessionLite[] | undefined => {
    try {
        if (!value) {
            return;
        }
        const parsedValue = JSON.parse(uint8ArrayToString(Uint8Array.fromBase64(value, { alphabet: 'base64url' })));
        if (!Array.isArray(parsedValue)) {
            return;
        }
        const parsedArray = parsedValue.map(fromItem).filter(isTruthy);
        if (parsedArray.length) {
            return parsedArray;
        }
    } catch (e) {}
};

export const writeAccountSessions = (persistedSessions = getPersistedSessions()) => {
    const sessions = persistedSessions
        // Remove oauth sessions, we don't want them to be visible in the in-app account switcher
        .filter((session) => session.source !== SessionSource.Oauth)
        .map(getMinimalPersistedSession);
    syncToCookie(to(sessions));
};

export const readAccountSessions = () => {
    const cookieValue = getCookie(cookieName);
    return from(cookieValue);
};

/**
 * The purpose of this function is to extend the lifetime of the iaas cookie.
 * It rewrites whatever value it currently has so that the expiration increases.
 * This is important because browsers cap the maximum expiration of a cookie, where
 * certain browsers like brave or safari cap them at 7 days.
 */
export const updateAccountSessions = () => {
    const cookieValue = getCookie(cookieName);
    if (!cookieValue) {
        return false;
    }
    syncToCookie(cookieValue);
    return true;
};
